import type { AxiosInstance } from 'axios';
import { createApiInstance } from './createApiInstance';

/**
 * Register named HTTP clients here. Each key points at a `VITE_*` base URL in `.env`.
 * Add a row + env var when a new service is introduced.
 */
export const API_CLIENT_ENV = {
  gateway: 'GATEWAY_BASE_URL',
  commService: 'COMM_SERVICE_BASE_URL',
  genius: 'GENIUS_URL',
  oneappAdminBFF: 'ONEAPP_ADMIN_BFF'
} as const;

export type ApiClientId = keyof typeof API_CLIENT_ENV;

const cache = new Map<ApiClientId, AxiosInstance>();

function resolveBaseUrl(id: ApiClientId): string {
  const envKey = API_CLIENT_ENV[id];
  
  const raw = import.meta.env[envKey as keyof ImportMetaEnv];
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  
  if (trimmed) return trimmed;

  if (import.meta.env.DEV) {
    console.warn(`[api] ${envKey} is not set for client "${id}". Ensure it is defined in your .env files.`);
    // Fallback path for local dev proxy based on the ID (e.g., /gateway)
    return `/${id}`; 
  }

  // Never ship a build with a missing base URL — fail loudly instead of silently
  // calling the frontend's own origin (relative '') in staging/preprod/production.
  throw new Error(
    `[api] Missing required environment variable "${envKey}" for API client "${id}". ` +
      `Set it in the appropriate .env.<mode> file before building.`
  );
}

/** Memoised Axios instance per logical client / base URL. */
export function getApiClient(id: ApiClientId): AxiosInstance {
  let instance = cache.get(id);
  if (!instance) {
    instance = createApiInstance(resolveBaseUrl(id));
    cache.set(id, instance);
  }
  return instance;
}
